import BirthdayHeader from "@/components/birthday-reminder/BirthdayHeader";
import BirthdayFooter from "@/components/birthday-reminder/BirthdayFooter";
import AppStoreButtons from "@/components/birthday-reminder/AppStoreButtons";
import FeatureCard from "@/components/birthday-reminder/FeatureCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Bell,
  Users,
  Calendar,
  Cake,
  MessageCircle,
  Shield,
  ArrowRight,
  Check,
  Lock,
  Smartphone,
} from "lucide-react";

export default function BirthdayReminderHome() {
  const features = [
    {
      icon: Bell,
      title: "Smart Reminders",
      description: "Get notified at the perfect time - morning of, day before, or week ahead. Never miss a celebration again.",
    },
    {
      icon: Users,
      title: "Import from Contacts",
      description: "Seamlessly import birthdays from your contacts with intelligent duplicate detection.",
    },
    {
      icon: Calendar,
      title: "Beautiful Calendar",
      description: "See all birthdays at a glance with a visual monthly calendar view.",
    },
    {
      icon: Cake,
      title: "Automatic Age Calculation",
      description: "Always know how old they're turning with automatic age tracking.",
    },
    {
      icon: MessageCircle,
      title: "Quick Messages",
      description: "Auto-drafted birthday wishes ready to send with one tap.",
    },
    {
      icon: Shield,
      title: "100% Private",
      description: "All data stays on your device. No accounts, no cloud, no tracking.",
    },
  ];

  const steps = [
    {
      number: "1",
      title: "Add Birthdays",
      description: "Manually or import from contacts",
    },
    {
      number: "2",
      title: "Set Reminders",
      description: "Choose when you want to be notified",
    },
    {
      number: "3",
      title: "Never Forget",
      description: "Get timely reminders and celebrate",
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <BirthdayHeader currentPath="/birthdayreminder" />

      {/* Hero Section */}
      <section className="py-20 md:py-32 px-4 bg-gradient-to-b from-pink-50 to-purple-50 dark:from-gray-900 dark:to-gray-800">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <Badge variant="secondary" className="px-4 py-1 text-sm">
                🎉 Free Forever • No Sign-Up Required
              </Badge>
              <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight">
                Never Forget a<br />
                <span className="bg-gradient-to-r from-pink-500 to-purple-600 bg-clip-text text-transparent">
                  Birthday Again
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-muted-foreground max-w-3xl mx-auto">
                Track friends & family, get reminders, and send messages effortlessly. 
                <span className="font-semibold text-foreground"> 100% private</span> — your data never leaves your device.
              </p>
            </div>

            <AppStoreButtons size="lg" className="justify-center" />

            <div className="flex items-center justify-center space-x-6 text-sm text-muted-foreground">
              <div className="flex items-center space-x-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>No accounts</span>
              </div>
              <div className="flex items-center space-x-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>No tracking</span>
              </div>
              <div className="flex items-center space-x-2">
                <Check className="w-5 h-5 text-green-500" />
                <span>100% free</span>
              </div>
            </div>
          </div>

          {/* Mockup Placeholder */}
          <div className="mt-16 relative">
            <div className="max-w-4xl mx-auto bg-gradient-to-br from-pink-500 to-purple-600 rounded-3xl shadow-2xl p-8 aspect-video flex items-center justify-center">
              <div className="text-center text-white space-y-4">
                <Smartphone className="w-20 h-20 mx-auto opacity-50" />
                <p className="text-sm opacity-75">App Screenshot Preview</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Statement */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center space-y-8">
          <h2 className="text-3xl md:text-4xl font-bold">We've All Been There...</h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 bg-muted/50 rounded-2xl">
              <p className="text-lg text-muted-foreground">
                😰 Forgot your sister's birthday last year
              </p>
            </div>
            <div className="p-6 bg-muted/50 rounded-2xl">
              <p className="text-lg text-muted-foreground">
                📱 Lost track after leaving Facebook
              </p>
            </div>
            <div className="p-6 bg-muted/50 rounded-2xl">
              <p className="text-lg text-muted-foreground">
                🤯 Too many birthdays to remember
              </p>
            </div>
            <div className="p-6 bg-muted/50 rounded-2xl">
              <p className="text-lg text-muted-foreground">
                😬 Embarrassing moments you'd rather avoid
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl md:text-5xl font-bold">Everything You Need</h2>
            <p className="text-xl text-muted-foreground">
              Powerful features designed to help you never miss a birthday
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <FeatureCard
                key={index}
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
              />
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-5xl">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-3xl md:text-5xl font-bold">How It Works</h2>
            <p className="text-xl text-muted-foreground">
              Get started in just 3 simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="text-center space-y-4">
                <div className="w-16 h-16 bg-gradient-to-br from-pink-500 to-purple-600 rounded-full flex items-center justify-center text-2xl font-bold text-white mx-auto">
                  {step.number}
                </div>
                <h3 className="text-2xl font-semibold">{step.title}</h3>
                <p className="text-muted-foreground">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Privacy Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-pink-50 to-purple-50 dark:from-gray-900 dark:to-gray-800">
        <div className="container mx-auto max-w-4xl text-center space-y-8">
          <div className="w-20 h-20 bg-gradient-to-br from-pink-500 to-purple-600 rounded-3xl flex items-center justify-center mx-auto">
            <Lock className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-3xl md:text-5xl font-bold">Your Data Stays Yours</h2>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            We believe in complete privacy. That's why Birthday Reminder stores everything locally on your device. 
            No servers, no accounts, no tracking.
          </p>

          <div className="grid md:grid-cols-2 gap-6 pt-8">
            <div className="p-6 bg-background rounded-2xl shadow-lg">
              <Check className="w-8 h-8 text-green-500 mb-3 mx-auto" />
              <h3 className="font-semibold mb-2">Local Storage Only</h3>
              <p className="text-sm text-muted-foreground">
                All birthdays and data stored on your device
              </p>
            </div>
            <div className="p-6 bg-background rounded-2xl shadow-lg">
              <Check className="w-8 h-8 text-green-500 mb-3 mx-auto" />
              <h3 className="font-semibold mb-2">No Tracking</h3>
              <p className="text-sm text-muted-foreground">
                Zero analytics or user behavior monitoring
              </p>
            </div>
            <div className="p-6 bg-background rounded-2xl shadow-lg">
              <Check className="w-8 h-8 text-green-500 mb-3 mx-auto" />
              <h3 className="font-semibold mb-2">No User Accounts</h3>
              <p className="text-sm text-muted-foreground">
                No sign-up, login, or personal information required
              </p>
            </div>
            <div className="p-6 bg-background rounded-2xl shadow-lg">
              <Check className="w-8 h-8 text-green-500 mb-3 mx-auto" />
              <h3 className="font-semibold mb-2">You're in Control</h3>
              <p className="text-sm text-muted-foreground">
                Delete the app, delete your data - it's that simple
              </p>
            </div>
          </div>

          <Button variant="outline" size="lg" asChild>
            <a href="/birthdayreminder/privacy">
              Read Our Privacy Policy <ArrowRight className="w-4 h-4 ml-2" />
            </a>
          </Button>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 px-4">
        <div className="container mx-auto max-w-4xl text-center space-y-8">
          <h2 className="text-3xl md:text-5xl font-bold">
            Start Remembering Birthdays Today
          </h2>
          <p className="text-xl text-muted-foreground">
            Join thousands who never miss a birthday
          </p>
          <AppStoreButtons size="lg" className="justify-center" />
          <p className="text-sm text-muted-foreground">
            Free forever • iOS 13+ & Android 5.0+ • No credit card required
          </p>
        </div>
      </section>

      <BirthdayFooter />
    </div>
  );
}

